/**
 * Calificación de opinión PREGUNTA POR PREGUNTA
 * (`/api/calificaciones-pregunta`).
 *
 * Cada pregunta apunta a su indicador, de modo que el par pregunta-respuesta
 * calificado queda reutilizable. Es el carril alternativo a calificar el
 * formulario completo con una sola nota.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import { http } from '@/services/http'
import type { CriterioEvaluacion } from '@/interfaces/CriterioEvaluacion'

/** Calificación previa que se puede adoptar para una pregunta. */
export interface SugerenciaCalificacion {
  id: number
  calificacion: number
  enunciado: string
  total_respuestas: number
  periodo?: string | null
  id_ejecucion?: number | null
  grupo_interes?: string | null
  calificado_por?: string | null
  calificado_en?: string | null
}

/** Reparto de respuestas por opción: es lo que se contrasta con la rúbrica. */
export interface DistribucionRespuesta {
  opcion: string
  cantidad: number
  porcentaje: number
}

/** De qué formulario viene la pregunta: un indicador puede tener varias. */
export interface FormularioDeOrigen {
  id_configuracion: number
  titulo?: string | null
  instrumento?: string | null
  id_formulario_google?: number | null
  grupo_interes?: string | null
  /** Sin grupo propio: la pregunta entra en todos los formularios. */
  general: boolean
}

/** Cómo se llegó a la nota calculada, para poder mostrarlo. */
export interface DesgloseNota {
  respuestas_consideradas: number
  puntaje_maximo_opcion: number
  puntaje_total: number
  puntaje_apreciado: number
  porcentaje_cumplimiento: number
  escala_max: number
  conteo: Array<{
    opcion: string
    cantidad: number
    puntos_por_respuesta: number
    subtotal: number
  }>
  opciones_no_reconocidas: string[]
}

/**
 * Nota calculada desde la distribución con la tabla de valoración.
 * `nota` null = no se pudo calcular; `motivo` dice por qué.
 */
export interface NotaSugerida {
  nota: number | null
  motivo: string | null
  desglose: DesgloseNota | null
}

/** Forma con la que el API de calificacion opinion pregunta devuelve este dato. */
export interface PreguntaCalificable {
  id_pregunta: number
  enunciado: string
  tipo_respuesta?: string | null
  grupo_interes?: string | null
  formulario?: FormularioDeOrigen | null
  nota_sugerida?: NotaSugerida | null
  respuestas: { total: number; promedio: number | null; distribucion: DistribucionRespuesta[] }
  sugerencias: SugerenciaCalificacion[]
}

/** Forma con la que el API de calificacion opinion pregunta devuelve este dato. */
export interface IndicadorCalificable {
  id_indicador: number
  indicador: string
  preguntas: PreguntaCalificable[]
}

/** Forma con la que el API de calificacion opinion pregunta devuelve este dato. */
export interface AspectoCalificable {
  id_componente?: number | null
  ordinal?: string | null
  aspecto: string
  indicadores: IndicadorCalificable[]
}

/** Forma con la que el API de calificacion opinion pregunta devuelve este dato. */
export interface VistaCalificacion {
  configuracion: Record<string, unknown>
  /** `formulario_completo` no se califica aquí: lleva nota única en la ficha. */
  modalidad?: 'por_pregunta' | 'formulario_completo' | null
  aspectos: AspectoCalificable[]
  rubrica: CriterioEvaluacion | null
}

/** Forma con la que el API de calificacion opinion pregunta devuelve este dato. */
export interface ContactoGrupo {
  id: number
  nombre?: string | null
  email: string
  /** Hasta cuándo el correo sigue afiliado al área. null = sin caducidad. */
  vigencia_desde?: string | null
  vigencia_hasta?: string | null
  ultimo_uso_at?: string | null
}

/**
 * Consulta calificacion opinion pregunta · petición `GET`.
 *
 * @returns VistaCalificacion | null
 */
export async function getVistaCalificacionService(
  idConfiguracion: number,
  idEjecucion?: number | null
): Promise<VistaCalificacion | null> {
  const query = idEjecucion ? `?id_ejecucion=${idEjecucion}` : ''
  const r = await http.get<VistaCalificacion>(
    `/api/configuraciones-instrumento/${idConfiguracion}/calificacion${query}`
  )
  return r.ok ? (r.data ?? null) : null
}

/**
 * Califica calificacion opinion pregunta.
 */
export async function calificarPreguntasService(
  idConfiguracion: number,
  payload: {
    id_usuario: number
    id_ejecucion?: number | null
    observaciones?: string | null
    justificacion?: string | null
    calificaciones: Array<{ id_pregunta: number; calificacion: number }>
  }
): Promise<{ ok: boolean; mensaje?: string; error?: string }> {
  const r = await http.post<{ message: string }>(
    `/api/configuraciones-instrumento/${idConfiguracion}/calificar-preguntas`,
    payload
  )
  if (!r.ok) {
    const primero = Object.values(r.errors ?? {}).flat()[0]
    return { ok: false, error: primero ?? 'No se pudo calificar.' }
  }
  return { ok: true, mensaje: r.data?.message }
}

/** Correos ya usados con ese grupo de interés en esa área. */
export async function getContactosGrupoService(
  idGrupoInteres: number,
  params: { id_programa?: number | null; id_ejecucion?: number | null }
): Promise<ContactoGrupo[]> {
  const query = params.id_programa
    ? `?id_programa=${params.id_programa}`
    : params.id_ejecucion
      ? `?id_ejecucion=${params.id_ejecucion}`
      : ''
  if (!query) return []
  const r = await http.get<ContactoGrupo[]>(`/api/grupos-interes/${idGrupoInteres}/contactos${query}`)
  return r.ok ? (r.data ?? []) : []
}

/**
 * Crea calificacion opinion pregunta.
 */
export async function registrarContactosGrupoService(
  idGrupoInteres: number,
  payload: {
    id_programa?: number | null
    id_ejecucion?: number | null
    destinatarios: Array<{ email: string; nombre?: string | null }>
  }
): Promise<boolean> {
  const r = await http.post(`/api/grupos-interes/${idGrupoInteres}/contactos`, payload)
  return r.ok
}

/** Formularios históricos reutilizables: solo los de la misma área. */
export async function getFormulariosReutilizablesService(
  idEjecucion: number,
  excluir?: number | null
): Promise<Array<Record<string, unknown>>> {
  const query = excluir ? `?excluir=${excluir}` : ''
  const r = await http.get<Array<Record<string, unknown>>>(
    `/api/ejecuciones/${idEjecucion}/formularios-reutilizables${query}`
  )
  return r.ok ? (r.data ?? []) : []
}
